import type { LegalBlock, LegalSection } from "@/mocks/legalPages";

/**
 * 法務・案内系ページの Article レイアウト。
 * 見出し構造と、正式本文（段落・番号付きリスト・連絡先）を差し込むためのレイアウトのみを提供する。
 * - sections が空の場合は fallback（控えめな案内）を表示する。
 * - 見出しが空文字のセクションは見出しを描画しない（前文など）。
 * - content が指定されている場合はその順序どおりに描画する。未指定なら paragraphs を使用。
 * - 段落が未確定のセクションは「—」を控えめに表示する。
 */
function SectionBody({ section }: { section: LegalSection }) {
  if (section.content && section.content.length > 0) {
    return (
      <div className="mt-5 space-y-5">
        {section.content.map((block, index) => (
          <Block key={index} block={block} />
        ))}
      </div>
    );
  }

  if (section.paragraphs.length > 0) {
    return (
      <div className="mt-5 space-y-4">
        {section.paragraphs.map((paragraph, index) => (
          <p key={index} className="text-[13px] leading-[2.2] text-foreground-700 md:text-sm">
            {paragraph}
          </p>
        ))}
      </div>
    );
  }

  return <p className="mt-5 text-[13px] leading-[2.2] text-foreground-500 md:text-sm">—</p>;
}

function Block({ block }: { block: LegalBlock }) {
  if (block.type === "list") {
    return (
      <ol className="space-y-3">
        {block.items.map((item, index) => (
          <li
            key={index}
            className="flex gap-3.5 text-[13px] leading-[2] text-foreground-700 md:text-sm"
          >
            <span className="shrink-0 tabular-nums text-foreground-500">{index + 1}.</span>
            <span>{item}</span>
          </li>
        ))}
      </ol>
    );
  }

  if (block.type === "info") {
    return (
      <div className="space-y-1.5">
        {block.lines.map((line, index) => (
          <p key={index} className="text-[13px] leading-[2] text-foreground-700 md:text-sm">
            {line}
          </p>
        ))}
      </div>
    );
  }

  return <p className="text-[13px] leading-[2.2] text-foreground-700 md:text-sm">{block.text}</p>;
}

export default function LegalArticle({
  sections,
  fallback = "",
}: {
  sections: LegalSection[];
  fallback?: string;
}) {
  if (sections.length === 0) {
    if (!fallback) {
      return null;
    }
    return (
      <p className="text-[13px] leading-[2.2] text-foreground-600 md:text-sm">{fallback}</p>
    );
  }

  return (
    <div className="space-y-14 md:space-y-16">
      {sections.map((section) => (
        <section key={section.id} id={section.id} data-anchor className="scroll-mt-[104px]">
          {section.heading ? (
            <h2 className="font-heading text-[20px] leading-[1.6] text-foreground-950 md:text-[24px]">
              {section.heading}
            </h2>
          ) : null}
          <SectionBody section={section} />
        </section>
      ))}
    </div>
  );
}